/**
 * Built-in virtual backgrounds.
 *
 * They are DRAWN, not downloaded: four procedural scenes rendered once into an
 * offscreen canvas. Shipping photographic backgrounds would add megabytes to
 * the bundle for something most people replace with their own image anyway, and
 * a gradient composites in a fraction of the time a 4K JPEG does.
 */
export interface BackgroundOption {
  readonly id: string;
  readonly label: string;
}

export const BUILTIN_BACKGROUNDS: readonly BackgroundOption[] = [
  { id: 'slate', label: 'Slate' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'sandstone', label: 'Sandstone' },
  { id: 'ink', label: 'Ink' },
];

interface Scene {
  readonly from: string;
  readonly to: string;
  readonly glow: string;
}

const SCENES: Readonly<Record<string, Scene>> = {
  slate: { from: '#e7ecf3', to: '#aebccf', glow: 'rgba(255,255,255,0.55)' },
  aurora: { from: '#123b63', to: '#0b1f38', glow: 'rgba(84,196,214,0.45)' },
  sandstone: { from: '#f3e6d4', to: '#cbaa85', glow: 'rgba(255,247,235,0.6)' },
  ink: { from: '#1b1d29', to: '#080a12', glow: 'rgba(120,110,220,0.35)' },
};

export const isBuiltinBackground = (id: string): boolean => id in SCENES;

/**
 * Renders one scene at the composite resolution. Cheap enough to redo whenever
 * the pipeline resizes, so nothing has to be cached across resolutions.
 */
export function renderBuiltinBackground(
  id: string,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  const scene = SCENES[id];
  if (!scene) return null;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const linear = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  linear.addColorStop(0, scene.from);
  linear.addColorStop(1, scene.to);
  ctx.fillStyle = linear;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const radius = Math.max(canvas.width, canvas.height) * 0.7;
  const radial = ctx.createRadialGradient(
    canvas.width * 0.72, canvas.height * 0.18, 0,
    canvas.width * 0.72, canvas.height * 0.18, radius,
  );
  radial.addColorStop(0, scene.glow);
  radial.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = radial;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const vignette = ctx.createRadialGradient(
    canvas.width / 2, canvas.height / 2, radius * 0.35,
    canvas.width / 2, canvas.height / 2, radius,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.22)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return canvas;
}
